/**
 * `SillMark` — the compact square Sill brand mark for Sill's own visits
 * (site scanner, platform detection, install checks …) in the visits list,
 * grouped rows, detail header and Overview feed.
 *
 * The brand is wordmark-only (`sill.` in Instrument Serif with a lime dot,
 * ADR-0012); at 14–16 px the full wordmark is unreadable, so the mark is
 * the wordmark's first letter and its lime dot on a near-black tile, like
 * an app icon. The tile is the same in both themes (a brand mark does not
 * invert); in dark mode a hairline border separates it from the page.
 *
 * Identity rule (visits panel Step A): this glyph says "says it is Sill",
 * exactly like a brand logo. The Confirmed shield is drawn separately and
 * only when `identityState()` says confirmed.
 */

import { useTheme } from '../../lib/theme.js';

const TILE = '#141312';
const LETTER = '#F2EEE8';
const LIME = '#D9FF3F';
const DARK_EDGE = '#3A3530';
/** Lower-case "s" from Instrument Serif 400 (unitsPerEm 1000). */
const S_GLYPH =
  'M131 -9Q108 -9 82 -3.5Q56 2 40 10Q31 15 31 27L28 139Q28 156 41 156Q52 156 56 140Q73 69 97.5 43Q122 17 157 17Q186 17 205 39.5Q224 62 224 102Q224 142 202 173Q180 204 128 241Q76 276 52 311.5Q28 347 28 389Q28 446 68 481Q108 516 174 516Q194 516 213 511.5Q232 507 247 497Q256 491 256 481L258 371Q258 356 246 356Q239 356 236 360.5Q233 365 231 372Q211 437 191 462.5Q171 488 142 488Q116 488 99 470Q82 452 82 425Q82 390 103 364Q124 338 179 300Q229 264 254.5 222.5Q280 181 280 132Q280 68 239 29.5Q198 -9 131 -9Z';

export function SillMark({ size = 14 }: { size?: number }): JSX.Element {
  const { theme } = useTheme();
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 16 16"
      role="img"
      aria-label="Sill"
      data-testid="sill-mark"
      style={{ flexShrink: 0 }}
    >
      <rect
        x="0.5"
        y="0.5"
        width="15"
        height="15"
        rx="3.5"
        fill={TILE}
        stroke={theme === 'dark' ? DARK_EDGE : TILE}
        strokeWidth="1"
      />
      {/* Instrument Serif "s" outline (OFL), font units, baseline-flipped,
          so the mark carries no selectable text and needs no font load. */}
      <path d={S_GLYPH} transform="translate(3.35 12.6) scale(0.019 -0.019)" fill={LETTER} />
      <circle cx="11.55" cy="11.05" r="1.55" fill={LIME} />
    </svg>
  );
}
